import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaPlay, FaHeadphones } from "react-icons/fa";

function ToeflListeningIntro({ mock = 1 }) {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleStartTest = () => {
    navigate(mock === 1 ? "/quiz/toefl/listening" : `/quiz/toefl-mock${mock}/listening`);
  };

  const conditions = [
    <>
      The Listening section (TOEFL iBT 2026 format) has four task types:
      <br />
      Listen and Choose a Response – pick the best reply to one speaker
      <br />
      Listen to a Conversation – everyday and campus conversations
      <br />
      Listen to an Announcement – campus and public notices
      <br />
      Listen to an Academic Talk – short lectures
    </>,
    "The section is adaptive: your Module 1 answers decide whether Module 2 is harder or easier.",
    'Click "Play Audio" to hear each recording. It plays only once, and the questions appear when it ends.',
    "You must answer each question before moving on, and you cannot return to previous questions.",
    "Module 1 lasts 15 minutes and Module 2 lasts 12 minutes. Your score is reported on the 1–6 band scale.",
    "Use headphones and Google Chrome or Microsoft Edge for the best audio.",
  ];

  return (
    <>
      <style>{`
        .listening-container {
          min-height: 100vh;
          background: #fafafa;
          padding: 2rem;
          padding-top: 96px;
          display: flex;
          align-items: flex-start;
          justify-content: center;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        .content-wrapper {
          width: 100%;
          max-width: 680px;
          background: #ffffff;
          border-radius: 20px;
          padding: 3rem 3.25rem;
          border: 1px solid #e8e8e8;
          box-shadow: 0 4px 18px rgba(0, 0, 0, 0.07);
        }

        .icon-badge {
          width: 68px;
          height: 68px;
          border-radius: 16px;
          background: rgba(10, 137, 78, 0.08);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem auto;
        }

        .icon-badge svg {
          font-size: 1.9rem;
          color: #0a894e;
        }

        .main-title {
          font-size: 2.1rem;
          font-weight: 800;
          margin: 0 0 0.6rem 0;
          color: #0a894e;
          text-align: center;
          letter-spacing: -0.01em;
        }

        .subtitle {
          font-size: 1.05rem;
          color: #666;
          text-align: center;
          margin-bottom: 2.25rem;
        }

        .section-title {
          font-size: 0.85rem;
          font-weight: 700;
          color: #0a894e;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin: 0 0 1rem 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .section-title svg {
          font-size: 1rem;
        }

        .conditions-box {
          background: #f7f8f7;
          border-radius: 14px;
          padding: 0.25rem 1.5rem;
          border: 1px solid #e5e5e5;
          margin-bottom: 2.25rem;
        }

        .conditions-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .condition-item {
          color: #333;
          padding: 1rem 0;
          font-size: 1rem;
          border-bottom: 1px solid #e8e8e8;
          display: flex;
          align-items: flex-start;
          line-height: 1.55;
        }

        .condition-item:last-child {
          border-bottom: none;
        }

        .condition-item::before {
          content: "";
          min-width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #0a894e;
          margin-right: 0.9rem;
          margin-top: 0.55rem;
          flex-shrink: 0;
        }

        .button-group {
          display: flex;
          gap: 1rem;
          justify-content: center;
        }

        .start-button {
          flex: 1;
          background: #0a894e;
          color: #fff;
          border: none;
          padding: 1.05rem 1.75rem;
          font-size: 1.05rem;
          font-weight: 600;
          border-radius: 12px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.7rem;
          transition: all 0.2s ease;
        }

        .start-button:hover {
          background: #087a44;
          transform: translateY(-2px);
          box-shadow: 0 8px 18px rgba(10, 137, 78, 0.28);
        }

        .exit-button {
          flex: 1;
          background: transparent;
          color: #444;
          border: 1px solid #d8d8d8;
          padding: 1.05rem 1.75rem;
          font-size: 1.05rem;
          font-weight: 600;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .exit-button:hover {
          background: #f5f5f5;
          border-color: #bbb;
          transform: translateY(-2px);
        }

        /* Responsive Design */
        @media (max-width: 720px) {
          .content-wrapper {
            max-width: 100%;
            padding: 2.25rem 1.75rem;
          }
        }

        @media (max-width: 560px) {
          .listening-container {
            padding: 1rem;
            padding-top: 76px;
          }

          .content-wrapper {
            padding: 1.75rem 1.5rem;
            border-radius: 16px;
          }

          .main-title {
            font-size: 1.6rem;
          }

          .subtitle {
            font-size: 0.95rem;
          }

          .condition-item {
            font-size: 0.92rem;
          }

          .button-group {
            flex-direction: column;
          }
        }
      `}</style>

      <div className="listening-container">
        <div className="content-wrapper">
          <div className="icon-badge">
            <FaHeadphones />
          </div>

          <h1 className="main-title">TOEFL Listening Test</h1>
          <p className="subtitle">
            This section measures your ability to understand spoken
            English in academic environments.
          </p>

          <h2 className="section-title">
            <FaHeadphones />
            Test Instructions
          </h2>

          <div className="conditions-box">
            <ul className="conditions-list">
              {conditions.map((condition, index) => (
                <li className="condition-item" key={index}>
                  {condition}
                </li>
              ))}
            </ul>
          </div>

          <div className="button-group">
            <button className="start-button" onClick={handleStartTest}>
              <FaPlay size={14} />
              <span>Start Test</span>
            </button>

            <button className="exit-button" onClick={() => navigate("/")}>
              Exit Module
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default ToeflListeningIntro;